import type { PageParticipant } from "../../../../shared/contracts/product-pages.js";
import { matchPlayer } from "./ggb-merge.js";
import type { ImportSummary } from "./ggb-merge.js";
import { has } from "./ggb-types.js";
import type { HoleValues, ParsedPlayer } from "./ggb-types.js";
import type { ContestFormat } from "../../../../domain/contest/contest-types.js";
import { validateImportMetadata } from "./ggb-review-validation.js";
import type { CourseTeeItem } from "../../../api/course-api.js";
import type { StakeChoice } from "../wizard-state.js";
import type { RoundConditionTag } from "../../../../domain/round/round-types.js";

/*
 * The editable review of parsed scorecards. Pure and DOM-free so the
 * assignment rules can be unit-tested; the round creation lives in
 * ggb-round.ts because it needs the workspace.
 */

/** Assignment value for "create a new guest with the parsed name". */
export const guestAssignment = "__guest__";

export interface ReviewCard {
  totals?: { gross: string; net: string; stableford: string };
  key: string;
  name: string;
  /** False when the name is the "Scorecard N" fallback. */
  named: boolean;
  hcp: number | null;
  /** Participant id, `guestAssignment`, or "" for "not in this round". */
  assign: string;
  /** Editable scores as typed; "" means missing. */
  scores: Record<number, string>;
  conf: HoleValues;
  teeName: string;
  teeId?: string;
  playingHandicap: string;
  par: Record<number, string>;
  si: Record<number, string>;
  reconstructed: number[];
}

export interface ReviewState {
  stake?: StakeChoice | null;
  conditions?: Record<string, RoundConditionTag[]>;
  detail?: "totals-only";
  holeCount?: 9 | 18;
  course: string;
  /** Chosen registry identity, or a deliberately named place for a new course. */
  courseId?: string;
  courseLocation?: string;
  teeId?: string;
  teeChoices?: CourseTeeItem[];
  playedTimeOfDay?: "morning" | "midday" | "evening";
  sourceImageHashes?: string[];
  holes: number[];
  playedAt: string;
  /** Absent on saved legacy reviews, which required an exact timestamp. */
  datePrecision?: "instant" | "date" | "unknown";
  utcOffset: string;
  format: ContestFormat | "";
  scoringBasis: "gross" | "net" | "";
  confirmed: boolean;
  cards: ReviewCard[];
  issues: string[];
}

export function importPlayerName(
  card: ReviewCard,
  participants: readonly PageParticipant[],
) {
  return (
    participants.find((player) => player.id === card.assign)?.displayName ??
    card.name
  );
}

/** Turns the merged players into the editable review model. */
export function buildReview(
  summary: ImportSummary,
  participants: readonly PageParticipant[],
  self: PageParticipant | undefined,
): ReviewState {
  const holes = holeSetOf(summary.players);
  const cards = summary.players.map((player, index) =>
    cardOf(player, index, holes),
  );
  autoAssign(cards, participants, self);
  return {
    course: summary.players.map((p) => p.course).find(Boolean) ?? "",
    holes,
    playedAt: summary.players.map((p) => p.playedDate).find(Boolean) ?? "",
    datePrecision: summary.players.some((p) => p.playedDate)
      ? "date"
      : "unknown",
    utcOffset: "",
    format: summary.players.every(
      (player) => player.scoreFormat === "stableford",
    )
      ? "stableford"
      : "",
    scoringBasis: summary.players.every(
      (player) => player.scoringBasis === "net",
    )
      ? "net"
      : "",
    confirmed: false,
    cards,
    issues: Array.from(new Set(summary.issues)),
  };
}

/** 1-18 when any card spans both nines, else the nine that was read. */
function holeSetOf(players: ParsedPlayer[]): number[] {
  const numbers = players.flatMap(
    (player) => player.visibleHoles ?? Object.keys(player.holes).map(Number),
  );
  const front = numbers.some((n) => n <= 9);
  const back = numbers.some((n) => n >= 10);
  const from = front ? 1 : 10;
  const count = front && back ? 18 : 9;
  return Array.from({ length: count }, (_, i) => from + i);
}

function cardOf(
  player: ParsedPlayer,
  index: number,
  holes: number[],
): ReviewCard {
  const scores: Record<number, string> = {};
  for (const hole of holes) {
    scores[hole] =
      has(player.holes, hole) &&
      !player.reconstructed.includes(hole) &&
      player.conf[hole] !== 0
        ? String(player.holes[hole])
        : "";
  }
  return {
    key: `card-${index}`,
    name: player.name ?? `Scorecard ${index + 1}`,
    named: player.name !== null,
    hcp: player.hcp,
    assign: "",
    scores,
    conf: { ...player.conf },
    teeName: "",
    playingHandicap: "",
    par: Object.fromEntries(
      holes.map((hole) => [
        hole,
        has(player.par, hole) ? String(player.par[hole]) : "",
      ]),
    ),
    si: Object.fromEntries(
      holes.map((hole) => [
        hole,
        has(player.si, hole) ? String(player.si[hole]) : "",
      ]),
    ),
    reconstructed: [...player.reconstructed],
  };
}

/**
 * Matched names go to their participant; a parsed name nobody matches
 * defaults to a new guest so the player gets a record they can claim later.
 * Unknown owners and ambiguous matches require a choice, including a lone card.
 */
function autoAssign(
  cards: ReviewCard[],
  participants: readonly PageParticipant[],
  self: PageParticipant | undefined,
): void {
  const taken = new Set<string>();
  const candidates = Array.from(
    new Map(
      [...participants, ...(self ? [self] : [])]
        .filter((participant) => participant.status === "active")
        .map((participant) => [participant.id, participant]),
    ).values(),
  );
  for (const card of cards) {
    if (!card.named) continue;
    const match = matchPlayer(card.name, candidates);
    if (match.kind === "ambiguous") continue;
    if (match.kind === "manual") {
      card.assign = guestAssignment;
      continue;
    }
    if (taken.has(match.id)) continue;
    card.assign = match.id;
    taken.add(match.id);
  }
}

/** Sum of recorded strokes; callers label incomplete cards as partial. */
export function cardGross(card: ReviewCard, holes: number[]): number {
  return holes.reduce((sum, hole) => sum + (Number(card.scores[hole]) || 0), 0);
}

/** The problem that blocks saving, or null when the review is complete. */
export function validateReview(
  review: ReviewState,
  selfId: string | undefined,
  allowMissingScores = false,
): string | null {
  const assigned = review.cards.filter((card) => card.assign !== "");
  if (assigned.length === 0)
    return "Assign at least one scorecard to a player.";
  if (assigned.length > 4) return "A round holds up to four players.";
  const ids = assigned
    .map((card) => card.assign)
    .filter((id) => id !== guestAssignment);
  if (new Set(ids).size !== ids.length)
    return "Each player can only receive one scorecard.";
  if (selfId && !ids.includes(selfId)) return "Pick which scorecard is yours.";
  for (const card of review.detail === "totals-only" ? [] : assigned) {
    for (const hole of review.holes) {
      if (allowMissingScores && !card.scores[hole]?.trim()) continue;
      if (card.scores[hole] === "P" && review.format === "stableford") continue;
      const value = Number(card.scores[hole]);
      if (!Number.isInteger(value) || value < 1 || value > 30)
        return `${card.name}: fill in hole ${hole} (1-30 strokes).`;
    }
  }
  return validateImportMetadata(review, assigned);
}
